import { ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { COMPOSITION_BUFFER_MODE, FormArray, FormBuilder, FormControl } from '@angular/forms';
import { ConfigConstants, isDefined } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil, withLatestFrom } from 'rxjs/operators';
import { updateCustomerIdentification } from '../../store/actions/customer-identification.actions';
import {
    getFormCustomerIdentificationBindingFactory,
    getFormParticipantCopyReadonly,
} from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { CustomerIdentification } from '../../types/customer-identification';
import { CustomerIdentificationInputField } from '../../types/customer-identification-input-field';

@Component({
    selector: 'pace-customer-identification-input-field',
    templateUrl: './customer-identification-input-field.component.html',
    styleUrls: ['./customer-identification-input-field.component.scss'],
    providers: [{ provide: COMPOSITION_BUFFER_MODE, useValue: false }],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerIdentificationInputFieldComponent implements OnInit, OnDestroy {
    @Input()
    public property: keyof CustomerIdentification;

    @Input()
    public parentForm: FormArray | undefined;

    private readOnly$: Observable<boolean>;

    public binding$: Observable<CustomerIdentificationInputField>;
    public inputFieldControl: FormControl;

    private destroyTrigger$ = new Subject<void>();
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly store: Store<FormsState>,
        private readonly formBuilder: FormBuilder,
    ) {
        this.readOnly$ = store.select(getFormParticipantCopyReadonly);
    }

    public ngOnInit(): void {
        const bindingSelector = getFormCustomerIdentificationBindingFactory({ property: this.property });
        this.binding$ = this.store.select(bindingSelector).pipe(filter(isDefined));

        this.inputFieldControl = this.formBuilder.control({ value: '', disabled: false });

        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.inputFieldControl);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'Error in InputFieldComponent: ngOnInit: parentForm.push: ',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    ),
                );
        }

        this.inputFieldControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                withLatestFrom(this.binding$),
                filter(
                    ([newValue, binding]: [string, CustomerIdentificationInputField]) =>
                        binding.xmlId !== '' && newValue !== binding.value,
                ),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([newValue, binding]: [string, CustomerIdentificationInputField]) => {
                this.store.dispatch(
                    updateCustomerIdentification({ xmlId: binding.xmlId, newValue, invalidateSignatures: true }),
                );
            });

        combineLatest([this.binding$, this.readOnly$])
            .pipe(takeUntil(this.destroyTrigger$))
            .subscribe(([binding, readOnly]) => {
                this.inputFieldControl.patchValue(binding.value, { emitEvent: false });
                this.inputFieldControl[readOnly ? 'disable' : 'enable']({ emitEvent: false });
            });
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.inputFieldControl);
            this.parentForm.removeAt(index);
        }
        this.destroyTrigger$.next();
    }
}
